import { useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import type { AnnouncementId, AnnouncementDisplayType } from '../types';

/**
 * Hook to track announcement analytics events via PostHog.
 */
export function useAnnouncementTracking(announcementId: AnnouncementId) {
  // Track whether impression has already been sent to avoid duplicates
  const impressionSentRef = useRef<Record<AnnouncementDisplayType, boolean>>({
    modal: false,
    banner: false,
  });

  const trackImpression = useCallback(
    (displayType: AnnouncementDisplayType) => {
      // Only track once per display type per session
      if (impressionSentRef.current[displayType]) {
        return;
      }

      try {
        posthog.capture('announcement_impression', {
          announcement_id: announcementId,
          display_type: displayType,
        });
        impressionSentRef.current[displayType] = true;
      } catch (error) {
        console.error('Error tracking announcement impression:', error);
      }
    },
    [announcementId]
  );

  const trackCtaClick = useCallback(
    (ctaType: 'primary' | 'secondary') => {
      try {
        posthog.capture('announcement_cta_click', {
          announcement_id: announcementId,
          cta_type: ctaType,
        });
      } catch (error) {
        console.error('Error tracking announcement CTA click:', error);
      }
    },
    [announcementId]
  );

  const trackDismiss = useCallback(
    (displayType: AnnouncementDisplayType) => {
      try {
        posthog.capture('announcement_dismissed', {
          announcement_id: announcementId,
          display_type: displayType,
        });
      } catch (error) {
        console.error('Error tracking announcement dismiss:', error);
      }
    },
    [announcementId]
  );

  return {
    trackImpression,
    trackCtaClick,
    trackDismiss,
  };
}
